// Client-facing Vessels & operating-limits settings for the SeaState console.
//
// Loads the tenant registry (vessels, operations, per-kind defaults, saved
// limits) and renders one block per vessel with an editable limits table, plus
// an operations catalog. No framework and no state library: every mutation
// posts to the typed API client and, on success, re-fetches the registry and
// re-renders the whole page. A 401 hands control back to the caller to send the
// user to the login screen.

import {
  getRegistry,
  putLimit,
  deleteLimit,
  createOperation,
  deleteOperation,
  setVesselOperations,
  createVessel,
  updateVessel,
  deleteVessel,
} from "../data/api.js";
import type {
  ApiRegistry,
  ApiVessel,
  ApiOperation,
  ApiLimit,
  ApiDefault,
  VesselInput,
} from "../data/api.js";
import {
  editLenParse,
  editLenResolve,
  editLenStr,
  getUnitSystem,
  lenUnit,
  subscribeUnits,
} from "../ui/units.js";
import {
  describeLimitReset,
  describeLimitSave,
  describeOperationAdd,
  describeOperationDelete,
  describeVesselAdd,
  describeVesselDelete,
  describeVesselEdit,
  describeVesselOpToggle,
  gatedSpecMutation,
} from "../ui/opsgate.js";

// --- validation limits (task-spec) ------------------------------------------

const WAVE_MIN = 0.25;
const WAVE_MAX = 10;
const WIND_MIN = 1;
const WIND_MAX = 60;
const HOURS_MIN = 1;
const HOURS_MAX = 96;

// Vessel-particular ranges — mirror the server's validation so the UI rejects
// bad input before the round-trip. `null` (blank field) is always allowed.
const VESSEL_RANGES = {
  loa_m: { min: 1, max: 500, label: "Length (LOA, m)", step: "0.01" },
  beam_m: { min: 1, max: 100, label: "Beam (m)", step: "0.01" },
  draft_m: { min: 0.1, max: 50, label: "Draft (m)", step: "0.01" },
  roll_period_s: { min: 1, max: 60, label: "Roll period (s)", step: "0.1" },
  cruise_kn: { min: 0.1, max: 40, label: "Cruise speed (kn)", step: "0.1" },
  max_kn: { min: 0.1, max: 60, label: "Max speed (kn)", step: "0.1" },
} as const;

type VesselNumField = keyof typeof VESSEL_RANGES;

// The metre-backed fields — the only ones the Units preference converts.
// Ranges, storage and the API stay metric; conversion happens at render and is
// undone at parse (ui/units.ts), with an untouched field resolving to the exact
// stored number so opening and saving never drifts a value.
const LEN_FIELDS: ReadonlySet<VesselNumField> = new Set(["loa_m", "beam_m", "draft_m"]);

function fieldLabel(f: VesselNumField): string {
  const label: string = VESSEL_RANGES[f].label;
  return LEN_FIELDS.has(f) && getUnitSystem() === "imperial" ? label.replace("m)", "ft)") : label;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Metres/lengths: up to two decimals with trailing zeros stripped. */
function fmtM(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, "");
}

function vesselParticulars(v: ApiVessel): string {
  const parts: string[] = [];
  if (v.loa_m > 0) parts.push(`${editLenStr(v.loa_m)} ${lenUnit()}`);
  if (v.beam_m > 0) parts.push(`beam ${editLenStr(v.beam_m)} ${lenUnit()}`);
  if (v.draft_m > 0) parts.push(`draft ${editLenStr(v.draft_m)} ${lenUnit()}`);
  if (v.cruise_kn > 0) parts.push(`${fmtM(v.cruise_kn)} kn cruise`);
  if (v.dp) parts.push("DP2");
  if (v.day_only) parts.push("daylight-only");
  if (v.nearshore) parts.push("nearshore");
  return parts.join(" · ");
}

/** The numbers a row should show: saved limit if present, else the kind default.
 *  The wave string renders in the active unit; `waveM` keeps the exact stored
 *  metric value behind it, so an untouched save never converts a round trip. */
interface Baseline {
  wave: string;
  waveM: number | null;
  wind: string;
  hours: string;
}

function baselineValues(limit: ApiLimit | undefined, def: ApiDefault | undefined): Baseline {
  const src = limit ?? def;
  if (src === undefined) return { wave: "", waveM: null, wind: "", hours: "" };
  return {
    wave: editLenStr(src.wave_m),
    waveM: src.wave_m,
    wind: String(src.wind_kn),
    hours: String(src.task_hours),
  };
}

function numInput(value: string, step: string): HTMLInputElement {
  const input = el("input", "mono");
  input.type = "number";
  input.step = step;
  input.value = value;
  input.style.width = "5.5em";
  return input;
}

// --- vessel particulars form (create + edit) --------------------------------

interface VesselForm {
  root: HTMLElement;
  /** Read + validate the fields into a VesselInput, or return an error string. */
  read(): { input: VesselInput } | { error: string };
  setError(message: string): void;
  clearError(): void;
}

/** A .fld-styled numeric field, blank when the value is null/0. Metre-backed
 *  fields render (and range-hint) in the active unit; `rendered` and `initial`
 *  let read() resolve an untouched field back to the exact stored value. */
function fldNum(f: VesselNumField, v: number | null): {
  wrap: HTMLLabelElement;
  input: HTMLInputElement;
  rendered: string;
  initial: number | null;
} {
  const spec = VESSEL_RANGES[f];
  const len = LEN_FIELDS.has(f);
  const wrap = el("label", "fld");
  wrap.appendChild(el("span", "lab", fieldLabel(f)));
  const input = el("input", "mono");
  input.type = "number";
  input.step = len && getUnitSystem() === "imperial" ? "0.1" : spec.step;
  input.min = len ? editLenStr(spec.min) : String(spec.min);
  input.max = len ? editLenStr(spec.max) : String(spec.max);
  const rendered = v !== null && v > 0 ? (len ? editLenStr(v) : fmtM(v)) : "";
  input.value = rendered;
  wrap.appendChild(input);
  return { wrap, input, rendered, initial: v !== null && v > 0 ? v : null };
}

/** A checkbox row with a label to its right. */
function checkField(label: string, checked: boolean): {
  wrap: HTMLLabelElement;
  input: HTMLInputElement;
} {
  const wrap = el("label", "vform-check");
  const input = el("input");
  input.type = "checkbox";
  input.checked = checked;
  wrap.append(input, el("span", undefined, label));
  return { wrap, input };
}

/**
 * Build the particulars form used both to create a new vessel and to edit an
 * existing one. `initial` is null for a fresh vessel.
 */
function buildVesselForm(initial: ApiVessel | null): VesselForm {
  const root = el("div", "vform");

  const nameWrap = el("label", "fld vform-wide");
  nameWrap.appendChild(el("span", "lab", "Vessel name"));
  const nameInput = el("input");
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.placeholder = "e.g. Brooks McCall";
  nameInput.value = initial?.name ?? "";
  nameWrap.appendChild(nameInput);

  const loa = fldNum("loa_m", initial?.loa_m ?? null);
  const beam = fldNum("beam_m", initial?.beam_m ?? null);
  const draft = fldNum("draft_m", initial?.draft_m ?? null);
  const roll = fldNum("roll_period_s", initial?.roll_period_s ?? null);
  const cruise = fldNum("cruise_kn", initial?.cruise_kn ?? null);
  const maxKn = fldNum("max_kn", initial?.max_kn ?? null);

  const dp = checkField("Dynamic positioning (DP)", initial?.dp ?? false);
  const dayOnly = checkField("Daylight-only", initial?.day_only ?? false);
  const nearshore = checkField("Nearshore-capable", initial?.nearshore ?? false);
  const checks = el("div", "vform-checks");
  checks.append(dp.wrap, dayOnly.wrap, nearshore.wrap);

  const gearWrap = el("label", "fld vform-wide");
  gearWrap.appendChild(el("span", "lab", "Gear / equipment"));
  const gearInput = el("textarea");
  gearInput.rows = 2;
  gearInput.placeholder = "Spec-sheet equipment summary (optional)";
  gearInput.value = initial?.gear ?? "";
  gearWrap.appendChild(gearInput);

  const err = el("p", "field-err");
  err.hidden = true;

  root.append(
    nameWrap,
    loa.wrap,
    beam.wrap,
    draft.wrap,
    roll.wrap,
    cruise.wrap,
    maxKn.wrap,
    checks,
    gearWrap,
    err,
  );

  const numFields: [VesselNumField, ReturnType<typeof fldNum>][] = [
    ["loa_m", loa],
    ["beam_m", beam],
    ["draft_m", draft],
    ["roll_period_s", roll],
    ["cruise_kn", cruise],
    ["max_kn", maxKn],
  ];

  return {
    root,
    setError(message: string): void {
      err.textContent = message;
      err.hidden = false;
    },
    clearError(): void {
      err.hidden = true;
    },
    read(): { input: VesselInput } | { error: string } {
      const name = nameInput.value.trim();
      if (name === "") return { error: "Vessel name is required." };
      if (name.length > 120) return { error: "Vessel name is too long." };

      const nums: Record<VesselNumField, number | null> = {
        loa_m: null,
        beam_m: null,
        draft_m: null,
        roll_period_s: null,
        cruise_kn: null,
        max_kn: null,
      };
      for (const [key, field] of numFields) {
        const raw = field.input.value.trim();
        if (raw === "") continue;
        const spec = VESSEL_RANGES[key];
        const len = LEN_FIELDS.has(key);
        // Metre-backed fields: an untouched field resolves to the exact stored
        // value; an edited one parses in the display unit and converts back.
        // Validation is against the metric range either way.
        const value = len
          ? field.initial !== null
            ? editLenResolve(raw, field.rendered, field.initial)
            : editLenParse(raw)
          : Number(raw);
        if (!Number.isFinite(value) || value < spec.min || value > spec.max) {
          const lo = len ? editLenStr(spec.min) : String(spec.min);
          const hi = len ? editLenStr(spec.max) : String(spec.max);
          return { error: `${fieldLabel(key)} must be ${lo}–${hi}.` };
        }
        nums[key] = value;
      }

      const gear = gearInput.value.trim();
      return {
        input: {
          name,
          loa_m: nums.loa_m,
          beam_m: nums.beam_m,
          draft_m: nums.draft_m,
          roll_period_s: nums.roll_period_s,
          cruise_kn: nums.cruise_kn,
          max_kn: nums.max_kn,
          dp: dp.input.checked,
          day_only: dayOnly.input.checked,
          nearshore: nearshore.input.checked,
          gear: gear === "" ? null : gear,
        },
      };
    },
  };
}

// --- page -------------------------------------------------------------------

export function mountVessels(
  container: HTMLElement,
  opts: { onUnauthorized: () => void },
): void {
  const reload = (): void => {
    void getRegistry().then((res) => {
      if (res.ok) {
        renderRegistry(container, res.data, reload, opts);
        return;
      }
      if (res.status === 401) {
        opts.onUnauthorized();
        return;
      }
      if (res.status === 503) {
        renderMessage(
          container,
          "Vessel settings need the database — it isn't configured on this deployment.",
        );
        return;
      }
      renderMessage(container, res.error);
    });
  };

  renderMessage(container, "Loading vessels…");
  reload();

  // A Metric/Imperial flip re-renders the whole page — the same re-fetch-and-
  // redraw every mutation already does — so fields, headers and particulars all
  // change together. The subscription frees itself once this mount is gone,
  // the same self-removal the settings panel's document listeners use.
  const unsub = subscribeUnits(() => {
    if (!container.isConnected) {
      unsub();
      return;
    }
    reload();
  });
}

function renderMessage(container: HTMLElement, message: string): void {
  const block = el("section", "block");
  block.appendChild(el("p", "note", message));
  container.replaceChildren(block);
}

function renderRegistry(
  container: HTMLElement,
  registry: ApiRegistry,
  reload: () => void,
  opts: { onUnauthorized: () => void },
): void {
  const frag = document.createDocumentFragment();

  frag.appendChild(headingBlock());
  for (const vessel of registry.vessels) {
    frag.appendChild(vesselBlock(vessel, registry, reload));
  }
  frag.appendChild(addVesselBlock(reload));
  frag.appendChild(catalogBlock(registry, reload));

  container.replaceChildren(frag);
}

function headingBlock(): HTMLElement {
  const block = el("section", "block");
  const bh = el("div", "bh");
  bh.appendChild(el("span", "bt", "Vessels & operating limits"));
  block.appendChild(bh);
  block.appendChild(
    el(
      "p",
      "note",
      "Limits set here drive the console's workability calls. Values marked " +
        "“default” are provisional presets and stay flagged until you replace them.",
    ),
  );
  return block;
}

// --- one vessel -------------------------------------------------------------

/** What deleting a vessel takes with it, by operation label. Migration 0001
 *  declares vessel_operations and vessel_operation_limits ON DELETE CASCADE, so
 *  the row the operator deletes is not the only row that goes — every limit
 *  ever tuned for that hull goes with it, unrecoverably. The registry is
 *  already loaded, so the real counts cost no request and the confirm dialog
 *  can name them instead of hinting at them. */
export function vesselCascade(
  vessel: ApiVessel,
  registry: ApiRegistry,
): { limits: string[]; linked: string[] } {
  const label = (id: number): string =>
    registry.operations.find((o) => o.id === id)?.label ?? `operation ${id}`;
  return {
    limits: registry.limits
      .filter((l) => l.vessel_id === vessel.id)
      .map((l) => label(l.operation_id)),
    linked: vessel.operation_ids.map(label),
  };
}

function vesselBlock(
  vessel: ApiVessel,
  registry: ApiRegistry,
  reload: () => void,
): HTMLElement {
  const block = el("section", "block");

  const bh = el("div", "bh");
  bh.appendChild(el("span", "bt", vessel.name));
  bh.appendChild(el("span", "bsub", vesselParticulars(vessel)));

  // Edit / Delete live at the right of the header. Edit reveals an inline
  // particulars form; Delete removes the vessel (and, by cascade, its limits).
  const actions = el("div", "bh-actions");
  const editBtn = el("button", "btn", "Edit");
  editBtn.type = "button";
  const delBtn = el("button", "btn", "Delete");
  delBtn.type = "button";
  actions.append(editBtn, delBtn);
  bh.appendChild(actions);
  block.appendChild(bh);

  if (vessel.gear !== null && vessel.gear !== "") {
    block.appendChild(el("div", "source vessel-gear", `Gear: ${vessel.gear}`));
  }

  // Inline edit form, hidden until Edit is pressed.
  const form = buildVesselForm(vessel);
  const editWrap = el("form", "vessel-edit");
  editWrap.hidden = true;
  const editActions = el("div", "vform-actions");
  const saveBtn = el("button", "btn primary", "Save changes");
  saveBtn.type = "submit";
  const cancelBtn = el("button", "btn", "Cancel");
  cancelBtn.type = "button";
  editActions.append(saveBtn, cancelBtn);
  editWrap.append(form.root, editActions);
  block.appendChild(editWrap);

  const setEditing = (on: boolean): void => {
    editWrap.hidden = !on;
    table.style.display = on ? "none" : "";
    editBtn.textContent = on ? "Editing…" : "Edit";
    editBtn.disabled = on;
  };

  editBtn.addEventListener("click", () => setEditing(true));
  cancelBtn.addEventListener("click", () => {
    form.clearError();
    setEditing(false);
  });

  editWrap.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const parsed = form.read();
    if ("error" in parsed) {
      form.setError(parsed.error);
      return;
    }
    form.clearError();
    saveBtn.disabled = true;
    // The particulars are spec: they feed the seakeeping and workability maths,
    // so the gate states this diff the way it states a limit's.
    const change = describeVesselEdit(vessel, parsed.input);
    void gatedSpecMutation(change, (pw) => updateVessel(vessel.id, parsed.input, pw)).then(
      (res) => {
        if (res === "cancelled") {
          saveBtn.disabled = false;
          return;
        }
        if (res.ok) {
          reload();
          return;
        }
        saveBtn.disabled = false;
        form.setError(res.error);
      },
    );
  });

  delBtn.addEventListener("click", () => {
    // What the cascade will actually take, counted from the registry already in
    // hand. The browser confirm stays as the pre-send guard, because the gate
    // tries the mutation bare first: a client with no operations password set
    // must not lose a hull to one unconfirmed click.
    const cascade = vesselCascade(vessel, registry);
    if (
      !confirm(
        `Delete vessel “${vessel.name}”? This also destroys ` +
          `${cascade.limits.length} saved limit(s) and unlinks ` +
          `${cascade.linked.length} operation(s). It cannot be undone.`,
      )
    ) {
      return;
    }
    delBtn.disabled = true;
    const change = describeVesselDelete(vessel.name, cascade.limits, cascade.linked);
    void gatedSpecMutation(change, (pw) => deleteVessel(vessel.id, pw)).then((res) => {
      if (res === "cancelled") {
        delBtn.disabled = false;
        return;
      }
      if (res.ok) reload();
      else {
        delBtn.disabled = false;
        alert(res.error);
      }
    });
  });

  const table = el("table", "lim-table");
  const thead = el("thead");
  const hr = el("tr");
  for (const label of ["", "Operation", `Wave ≤ (${lenUnit()})`, "Wind ≤ (kn)", "Task ≥ (h)", "Status", ""]) {
    hr.appendChild(el("th", undefined, label));
  }
  thead.appendChild(hr);
  table.appendChild(thead);

  const tbody = el("tbody");
  for (const op of registry.operations) {
    tbody.appendChild(operationRow(vessel, op, registry, reload));
  }
  table.appendChild(tbody);
  block.appendChild(table);

  return block;
}

function operationRow(
  vessel: ApiVessel,
  op: ApiOperation,
  registry: ApiRegistry,
  reload: () => void,
): HTMLElement {
  const enabled = vessel.operation_ids.includes(op.id);
  const limit = registry.limits.find(
    (l) => l.vessel_id === vessel.id && l.operation_id === op.id,
  );
  const def = op.kind !== null ? registry.defaults[op.kind] : undefined;
  const base = baselineValues(limit, def);

  const row = el("tr", enabled ? undefined : "vrow-dim");

  // 1) enable checkbox — toggling persists the vessel's operation set.
  const checkCell = el("td");
  const checkbox = el("input");
  checkbox.type = "checkbox";
  checkbox.checked = enabled;
  checkbox.addEventListener("change", () => {
    checkbox.disabled = true;
    const before = vessel.operation_ids;
    const ids = new Set(before);
    if (checkbox.checked) ids.add(op.id);
    else ids.delete(op.id);
    const next = [...ids];
    // Write the new set back onto the shared vessel BEFORE the request settles.
    // Every row in this table closes over the SAME vessel object and rebuilds the
    // set from operation_ids at click time. Ticking a second box before the first
    // reload lands would otherwise start from a snapshot that never saw the first
    // tick, and the second PUT — which replaces the whole set — would silently
    // undo it. The server is atomic and ownership-checked; the race is only here.
    vessel.operation_ids = next;
    const change = describeVesselOpToggle(vessel.name, op.label, checkbox.checked);
    void gatedSpecMutation(change, (pw) => setVesselOperations(vessel.id, next, pw)).then(
      (res) => {
        if (res !== "cancelled" && res.ok) {
          reload();
          return;
        }
        // Revert the optimistic toggle; surface a failure (a cancel is silent).
        vessel.operation_ids = before;
        checkbox.checked = enabled;
        checkbox.disabled = false;
        if (res !== "cancelled") setRowError(errCell, res.error);
      },
    );
  });
  checkCell.appendChild(checkbox);
  row.appendChild(checkCell);

  // 2) operation name + kind.
  const nameCell = el("td");
  nameCell.appendChild(el("span", undefined, op.label));
  nameCell.appendChild(el("span", "bsub", op.kind !== null ? ` ${op.kind}` : " bespoke"));
  row.appendChild(nameCell);

  // 3–5) numeric inputs.
  const waveInput = numInput(base.wave, getUnitSystem() === "imperial" ? "0.1" : "0.05");
  const windInput = numInput(base.wind, "1");
  const hoursInput = numInput(base.hours, "1");
  for (const input of [waveInput, windInput, hoursInput]) {
    input.disabled = !enabled;
    const cell = el("td");
    cell.appendChild(input);
    row.appendChild(cell);
  }

  // 6) status chip.
  const statusCell = el("td");
  const chip =
    limit !== undefined
      ? el("span", "chip ok", "custom")
      : el("span", "chip warn", "⚠ default");
  statusCell.appendChild(chip);
  row.appendChild(statusCell);

  // 7) actions + inline validation error.
  const actionCell = el("td");
  const saveBtn = el("button", "btn", "Save");
  saveBtn.type = "button";
  saveBtn.disabled = true;
  const resetBtn = el("button", "btn", "Reset");
  resetBtn.type = "button";
  const errCell = el("span", "field-err");
  errCell.hidden = true;

  const dirty = (): boolean =>
    waveInput.value !== base.wave ||
    windInput.value !== base.wind ||
    hoursInput.value !== base.hours;

  const refreshSave = (): void => {
    saveBtn.disabled = !enabled || !dirty();
  };
  for (const input of [waveInput, windInput, hoursInput]) {
    input.addEventListener("input", () => {
      errCell.hidden = true;
      refreshSave();
    });
  }

  saveBtn.addEventListener("click", () => {
    const parsed = validateRow(waveInput.value, windInput.value, hoursInput.value, base);
    if (parsed.error !== null) {
      setRowError(errCell, parsed.error);
      return;
    }
    saveBtn.disabled = true;
    // The gate's confirm dialog states the concrete change: the row's current
    // numbers (the saved limit, or the default it was showing) → the new ones.
    const change = describeLimitSave(
      vessel.name,
      op.label,
      limit ?? def ?? null,
      limit === undefined,
      parsed.values,
    );
    void gatedSpecMutation(change, (pw) => putLimit(vessel.id, op.id, parsed.values, pw)).then(
      (res) => {
        if (res === "cancelled") {
          saveBtn.disabled = false;
          return;
        }
        if (res.ok) {
          reload();
          return;
        }
        saveBtn.disabled = false;
        setRowError(errCell, res.error);
      },
    );
  });

  resetBtn.addEventListener("click", () => {
    if (limit === undefined) return; // Reset only renders on a custom row
    resetBtn.disabled = true;
    const change = describeLimitReset(vessel.name, op.label, limit, def ?? null);
    void gatedSpecMutation(change, (pw) => deleteLimit(vessel.id, op.id, pw)).then((res) => {
      if (res === "cancelled") {
        resetBtn.disabled = false;
        return;
      }
      if (res.ok) {
        reload();
        return;
      }
      resetBtn.disabled = false;
      setRowError(errCell, res.error);
    });
  });

  actionCell.appendChild(saveBtn);
  if (limit !== undefined) actionCell.appendChild(resetBtn);
  actionCell.appendChild(errCell);
  if (!enabled) {
    saveBtn.disabled = true;
    resetBtn.disabled = true;
  }
  row.appendChild(actionCell);

  return row;
}

function setRowError(errCell: HTMLElement, message: string): void {
  errCell.textContent = message;
  errCell.hidden = false;
}

interface ValidatedRow {
  values: { wave_m: number; wind_kn: number; task_hours: number };
  error: string | null;
}

function validateRow(wave: string, wind: string, hours: string, base: Baseline): ValidatedRow {
  // The wave field is the one the Units preference converts. Untouched text
  // resolves to the exact stored metric value (no round trip); edited text
  // parses in the display unit and converts back. The range check is metric.
  const wave_m =
    base.waveM !== null ? editLenResolve(wave, base.wave, base.waveM) : editLenParse(wave);
  const wind_kn = Number(wind);
  const task_hours = Number(hours);
  const zero = { wave_m: 0, wind_kn: 0, task_hours: 0 };

  if (wave === "" || wind === "" || hours === "") {
    return { values: zero, error: "All three limits are required." };
  }
  if (!Number.isFinite(wave_m) || wave_m < WAVE_MIN || wave_m > WAVE_MAX) {
    return {
      values: zero,
      error: `Wave must be ${editLenStr(WAVE_MIN)}–${editLenStr(WAVE_MAX)} ${lenUnit()}.`,
    };
  }
  if (!Number.isFinite(wind_kn) || wind_kn < WIND_MIN || wind_kn > WIND_MAX) {
    return { values: zero, error: `Wind must be ${WIND_MIN}–${WIND_MAX} kn.` };
  }
  if (!Number.isFinite(task_hours) || task_hours < HOURS_MIN || task_hours > HOURS_MAX) {
    return { values: zero, error: `Task must be ${HOURS_MIN}–${HOURS_MAX} h.` };
  }
  // Wind and task hours are whole numbers on the wire (Go decodes them into int),
  // so a decimal is rejected by the JSON decoder before any handler sees it — and
  // that rejection can only say "invalid request body", which tells the operator
  // nothing about which of the three boxes to fix. Wave height is genuinely
  // fractional and stays a float; these two are not.
  if (!Number.isInteger(wind_kn)) {
    return { values: zero, error: "Wind must be a whole number of knots." };
  }
  if (!Number.isInteger(task_hours)) {
    return { values: zero, error: "Task must be a whole number of hours." };
  }
  return { values: { wave_m, wind_kn, task_hours }, error: null };
}

// --- add a vessel -----------------------------------------------------------

function addVesselBlock(reload: () => void): HTMLElement {
  const block = el("section", "block");
  const bh = el("div", "bh");
  bh.appendChild(el("span", "bt", "Add a vessel"));
  block.appendChild(bh);
  block.appendChild(
    el(
      "p",
      "note",
      "Register another vessel for your fleet. Only a name is required; " +
        "particulars can be filled in now or edited later.",
    ),
  );

  const form = el("form", "vessel-edit");
  const builder = buildVesselForm(null);
  const actions = el("div", "vform-actions");
  const addBtn = el("button", "btn primary", "Add vessel");
  addBtn.type = "submit";
  actions.appendChild(addBtn);
  form.append(builder.root, actions);
  block.appendChild(form);

  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const parsed = builder.read();
    if ("error" in parsed) {
      builder.setError(parsed.error);
      return;
    }
    builder.clearError();
    addBtn.disabled = true;
    void gatedSpecMutation(describeVesselAdd(parsed.input), (pw) =>
      createVessel(parsed.input, pw),
    ).then((res) => {
      if (res === "cancelled") {
        addBtn.disabled = false;
        return;
      }
      if (res.ok) {
        reload();
        return;
      }
      addBtn.disabled = false;
      builder.setError(res.error);
    });
  });

  return block;
}

// --- operations catalog -----------------------------------------------------

function catalogBlock(registry: ApiRegistry, reload: () => void): HTMLElement {
  const block = el("section", "block");
  const bh = el("div", "bh");
  bh.appendChild(el("span", "bt", "Operations catalog"));
  block.appendChild(bh);
  block.appendChild(
    el("p", "note", "Operations available to every vessel above. Add or remove them here."),
  );

  for (const op of registry.operations) {
    block.appendChild(catalogRow(op, reload));
  }

  block.appendChild(addForm(registry, reload));
  return block;
}

function catalogRow(op: ApiOperation, reload: () => void): HTMLElement {
  const row = el("div", "catalog-form");
  row.appendChild(el("span", undefined, op.label));
  row.appendChild(el("span", "bsub", op.kind !== null ? op.kind : "bespoke"));

  const del = el("button", "btn", "Delete");
  del.type = "button";
  del.addEventListener("click", () => {
    if (!confirm(`Delete operation “${op.label}”?`)) return;
    del.disabled = true;
    void gatedSpecMutation(describeOperationDelete(op.label), (pw) =>
      deleteOperation(op.id, pw),
    ).then((res) => {
      if (res === "cancelled") {
        del.disabled = false;
        return;
      }
      if (res.ok) reload();
      else {
        del.disabled = false;
        alert(res.error);
      }
    });
  });
  row.appendChild(del);
  return row;
}

function addForm(registry: ApiRegistry, reload: () => void): HTMLElement {
  const form = el("form", "catalog-form");

  const labelInput = el("input");
  labelInput.type = "text";
  labelInput.required = true;
  labelInput.placeholder = "New operation label";

  const kindSelect = el("select", "mono");
  const bespoke = el("option", undefined, "Bespoke (no preset)");
  bespoke.value = "";
  kindSelect.appendChild(bespoke);
  for (const kind of Object.keys(registry.defaults)) {
    const opt = el("option", undefined, `${kind} — ${registry.defaults[kind].label}`);
    opt.value = kind;
    kindSelect.appendChild(opt);
  }

  const add = el("button", "btn", "Add");
  add.type = "submit";

  const err = el("span", "field-err");
  err.hidden = true;

  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const label = labelInput.value.trim();
    if (label === "") {
      err.textContent = "Enter a label.";
      err.hidden = false;
      return;
    }
    err.hidden = true;
    add.disabled = true;
    const kind = kindSelect.value === "" ? undefined : kindSelect.value;
    void gatedSpecMutation(describeOperationAdd(label, kind ?? null), (pw) =>
      createOperation(label, kind, pw),
    ).then((res) => {
      if (res === "cancelled") {
        add.disabled = false;
        return;
      }
      if (res.ok) reload();
      else {
        add.disabled = false;
        err.textContent = res.error;
        err.hidden = false;
      }
    });
  });

  form.append(labelInput, kindSelect, add, err);
  return form;
}
